import { mediaQueryLarge, isMobileBreakpoint } from '@theme/utilities';

class AccordionCustom extends HTMLElement {
  /** @type {HTMLDetailsElement} */
  get details() {
    const details = this.querySelector('details');
    if (!(details instanceof HTMLDetailsElement)) throw new Error('Details element not found');
    return details;
  }

  /** @type {HTMLElement} */
  get summary() {
    const summary = this.details.querySelector('summary');
    if (!(summary instanceof HTMLElement)) throw new Error('Summary element not found');
    return summary;
  }

  get #disableOnMobile() { return this.dataset.disableOnMobile === 'true'; }
  get #disableOnDesktop() { return this.dataset.disableOnDesktop === 'true'; }
  get #closeWithEscape() { return this.dataset.closeWithEscape === 'true'; }

  /** @returns {string | null} The group name this accordion belongs to */
  get #group() { return this.dataset.group ?? null; }

  #controller = new AbortController();

  connectedCallback() {
    const { signal } = this.#controller;

    this.#setDefaultOpenState();

    this.addEventListener('keydown', this.#handleKeyDown, { signal });
    this.summary.addEventListener('click', this.#handleClick, { signal });
    mediaQueryLarge.addEventListener('change', this.#handleMediaQueryChange, { signal });
  }

  disconnectedCallback() {
    this.#controller.abort();
  }

  /**
   * Closes all other accordions in the same group.
   */
  #closeOthersInGroup() {
    if (!this.#group) return;

    document
      .querySelectorAll(`accordion-custom[data-group="${this.#group}"]`)
      .forEach((accordion) => {
        if (accordion !== this && accordion instanceof AccordionCustom) {
          accordion.details.open = false;
        }
      });
  }

  /**
   * Handles the click event.
   * @param {MouseEvent} event
   */
  #handleClick = (event) => {
    const isMobile = isMobileBreakpoint();
    const isDesktop = !isMobile;

    if ((isMobile && this.#disableOnMobile) || (isDesktop && this.#disableOnDesktop)) {
      event.preventDefault();
      return;
    }

    // If this accordion is about to open, close siblings in the group.
    // `details.open` reflects the state *before* the click toggles it.
    if (!this.details.open) {
      this.#closeOthersInGroup();
    }
  };

  #handleMediaQueryChange = () => {
    this.#setDefaultOpenState();
  };

  #setDefaultOpenState() {
    const isMobile = isMobileBreakpoint();
    this.details.open =
      (isMobile && this.hasAttribute('open-by-default-on-mobile')) ||
      (!isMobile && this.hasAttribute('open-by-default-on-desktop'));
  }

  #handleKeyDown = (event) => {
    if (event.key === 'Escape' && this.#closeWithEscape) {
      event.preventDefault();
      this.details.open = false;
      this.summary.focus();
    }
  }
}

if (!customElements.get('accordion-custom')) {
  customElements.define('accordion-custom', AccordionCustom);
}